import { useState } from "react";
import { Modal, Button, Input, Steps, Alert, Typography, Space, message, Divider } from "antd";
import { CopyOutlined, KeyOutlined, UploadOutlined } from "@ant-design/icons";
import api from "../../../../../core/axios/axiosInstance";

const { Text, Paragraph, Title } = Typography;

const KeySetupModal = ({ open, onClose }) => {
  const [step, setStep]               = useState(0);
  const [publicKey, setPublicKey]     = useState("");
  const [privateKey, setPrivateKey]   = useState("");
  const [generating, setGenerating]   = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [customPublicKey, setCustomPublicKey] = useState("");

  const generateKeys = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/api/meta-flows/keys/generate");
      setPublicKey(data.publicKey);
      setPrivateKey(data.privateKey);
      setStep(1);
    } catch {
      message.error("Failed to generate keys");
    } finally {
      setGenerating(false);
    }
  };

  const uploadPublicKey = async () => {
    const keyToUpload = publicKey || customPublicKey;
    if (!keyToUpload) return message.error("Please generate or paste a public key.");
    setUploading(true);
    try {
      await api.post("/api/meta-flows/keys/upload-public", { publicKeyPem: keyToUpload });
      message.success("Public key uploaded to Meta");
      setStep(2);
    } catch (err) {
      message.error(err.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text).then(() => message.success("Copied!"));
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={<><KeyOutlined style={{ marginRight: 8 }} />Encryption Key Setup (Dynamic Flows)</>}
      width={700}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <Alert
        type="info"
        message="Required for Dynamic Flows only"
        description="Static flows (form with fixed options) don't need encryption. Dynamic flows (server-driven, prefilled data, cascading dropdowns) require an RSA key pair for end-to-end encryption."
        style={{ marginBottom: 16 }}
        showIcon
      />

      <Steps
        current={step}
        size="small"
        style={{ marginBottom: 20 }}
        items={[
          { title: "Generate Keys" },
          { title: "Upload to Meta" },
          { title: "Set Env Var" },
        ]}
      />

      {step === 0 && (
        <div>
          <Paragraph>
            Generate an RSA 2048-bit key pair. The <strong>public key</strong> goes to Meta.
            The <strong>private key</strong> stays secret on your server as an env var.
          </Paragraph>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Button type="primary" icon={<KeyOutlined />} onClick={generateKeys} loading={generating}
              style={{ background: "#7c3aed", borderColor: "#7c3aed" }}>
              Generate RSA Key Pair
            </Button>
            <Divider plain>OR paste your own public key</Divider>
            <Input.TextArea
              value={customPublicKey}
              onChange={(e) => setCustomPublicKey(e.target.value)}
              rows={6}
              placeholder="-----BEGIN PUBLIC KEY-----&#10;...&#10;-----END PUBLIC KEY-----"
            />
            {customPublicKey && (
              <Button onClick={() => { setPublicKey(customPublicKey); setStep(1); }}>
                Use This Key
              </Button>
            )}
          </Space>
        </div>
      )}

      {step === 1 && (
        <div>
          {publicKey && (
            <>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Public Key (upload to Meta):</div>
                <Input.TextArea value={publicKey} readOnly rows={6} />
                <Button size="small" icon={<CopyOutlined />} onClick={() => copyText(publicKey)} style={{ marginTop: 4 }}>
                  Copy Public Key
                </Button>
              </div>
              {privateKey && (
                <Alert
                  type="warning"
                  message="Save your private key immediately"
                  description={
                    <div>
                      <div style={{ marginBottom: 6 }}>This private key is shown only once. Copy and store it securely.</div>
                      <Input.TextArea value={privateKey} readOnly rows={6} />
                      <Button size="small" icon={<CopyOutlined />} onClick={() => copyText(privateKey)} style={{ marginTop: 4 }}>
                        Copy Private Key
                      </Button>
                    </div>
                  }
                  style={{ marginBottom: 12 }}
                />
              )}
            </>
          )}
          <Paragraph>
            Upload the public key to Meta so Meta can encrypt requests to your endpoint:
          </Paragraph>
          <Button type="primary" icon={<UploadOutlined />} onClick={uploadPublicKey} loading={uploading}
            style={{ background: "#7c3aed", borderColor: "#7c3aed" }}>
            Upload Public Key to Meta
          </Button>
        </div>
      )}

      {step === 2 && (
        <div>
          <Alert type="success" message="Public key uploaded to Meta successfully!" style={{ marginBottom: 16 }} showIcon />
          <Title level={5}>Final step: Set Private Key as Lambda Environment Variable</Title>
          <Paragraph>
            In your AWS Lambda function, add this environment variable:
          </Paragraph>
          <div style={{ background: "#f3f4f6", padding: 12, borderRadius: 6, marginBottom: 12, fontFamily: "monospace" }}>
            <div><strong>Key:</strong> <Text code>WABA_FLOW_PRIVATE_KEY</Text></div>
            <div style={{ marginTop: 4 }}><strong>Value:</strong> Your private key PEM (replace actual newlines with <Text code>\n</Text>)</div>
          </div>
          <Alert
            type="info"
            message="Lambda newline tip"
            description={
              <span>
                Lambda env vars can't contain real newlines. Replace every newline in your private key PEM with the literal <Text code>\n</Text> characters before pasting. Example:<br />
                <code>-----BEGIN PRIVATE KEY-----\nMIIEvAIBADA...\n-----END PRIVATE KEY-----</code>
              </span>
            }
          />
          <Divider />
          <Paragraph>
            The data exchange endpoint is automatically registered at:
            <br />
            <Text code>{window.location.origin}/api/meta-flows/endpoint</Text>
            <br />
            Set this as the <strong>endpoint_uri</strong> when creating dynamic flows.
          </Paragraph>
        </div>
      )}
    </Modal>
  );
};

export default KeySetupModal;
